import { useState } from 'react';
import { Loader2, ClipboardList } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Kv({ label, value, mono = false }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground min-w-[200px]">{label}:</span>
      <span className={mono ? 'font-mono' : ''}>{value ?? 'n/a'}</span>
    </div>
  );
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const CLASS_LABELS = {
  cancelled: 'Cancelled (PMS hides it)',
  deleted_in_pms: 'Deleted in PMS',
  duplicate_ingestion_two_feed: 'Duplicate ingestion (two feeds)',
  migration_leakage_no_hostaway_twin: 'Migration leakage (no Hostaway twin)',
  not_yet_migrated: 'Not yet migrated (legitimate)',
  test_synthetic: 'Test / synthetic',
  other: 'Other',
};

const CLASS_BADGE_VARIANT = {
  cancelled: 'secondary',
  deleted_in_pms: 'destructive',
  duplicate_ingestion_two_feed: 'destructive',
  migration_leakage_no_hostaway_twin: 'destructive',
  not_yet_migrated: 'outline',
  test_synthetic: 'secondary',
  other: 'outline',
};

const INCONCLUSIVE_VERDICTS = new Set([
  'inconclusive_missing_credentials',
  'inconclusive_api_errors',
  'inconclusive_pagination_capped',
  'inconclusive_fa_fetch_capped',
]);

const SIMILARITY_BADGE_VARIANT = {
  exact: 'outline',
  similar: 'outline',
  different: 'destructive',
  unknown: 'secondary',
};

export function UKBookingCensusPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [month, setMonth] = useState(currentMonth());

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.ukBookingCensus(month);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;
  const inconclusive = r && INCONCLUSIVE_VERDICTS.has(r.verdict);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-amber-600" />
          UK Booking Census (FA vs PMS)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only. Pulls FA's UK bookings for a month on the same basis as the Bookings-tab
          month bucket, live-fetches Hostaway's reservation list for the same window (all
          statuses, paginated to exhaustion), and three-way joins: exact (Hostaway-sourced FA
          rows vs a live id), mapped-stay (Smoobu-sourced FA rows matched to a live reservation
          via hostaway_listing_map on listing+date+nights -- never guest name), and the
          residual FA-only / PMS-only rows.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Month (YYYY-MM)</label>
            <input
              type="month"
              className="border rounded px-2 py-1 text-sm"
              value={month}
              onChange={e => setMonth(e.target.value)}
            />
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Run Census
          </Button>
        </div>

        {r && (
          <div className="space-y-4">
            <div className="bg-muted/50 rounded p-3 text-sm space-y-1">
              <Kv label="Month" value={r.month} mono />
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground min-w-[200px] text-sm">Verdict:</span>
                <Badge variant={r.verdict === 'ok' ? 'outline' : 'destructive'} className="text-xs">
                  {r.verdict}
                </Badge>
              </div>
              {r.note && <p className="text-xs text-muted-foreground">{r.note}</p>}
            </div>

            {inconclusive ? (
              <div className="text-sm text-destructive font-medium">
                FETCH_FAILED / inconclusive -- {r.verdict === 'inconclusive_fa_fetch_capped'
                  ? 'the FA-side reservation fetch hit its page cap.'
                  : 'the live PMS side did not complete cleanly.'} No FA-vs-PMS comparison is
                shown below; absence is not proven.
                {r.fa_fetch_pages && (
                  <div className="mt-2 overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-1 pr-2">Collection</th>
                          <th className="py-1 pr-2">Doc count</th>
                          <th className="py-1 pr-2">Capped</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.fa_fetch_pages.map((p, i) => (
                          <tr key={i} className="border-b last:border-b-0">
                            <td className="py-1 pr-2 font-mono">{p.collection}</td>
                            <td className="py-1 pr-2">{p.doc_count}</td>
                            <td className="py-1 pr-2">{String(p.capped)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {r.pms_fetch?.reservations_pages && (
                  <div className="mt-2 overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-1 pr-2">Page</th>
                          <th className="py-1 pr-2">Offset</th>
                          <th className="py-1 pr-2">HTTP status</th>
                          <th className="py-1 pr-2">Items</th>
                          <th className="py-1 pr-2">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.pms_fetch.reservations_pages.map((p, i) => (
                          <tr key={i} className="border-b last:border-b-0">
                            <td className="py-1 pr-2">{p.page_index}</td>
                            <td className="py-1 pr-2">{p.offset}</td>
                            <td className="py-1 pr-2">{p.http_status ?? 'n/a'}</td>
                            <td className="py-1 pr-2">{p.item_count}</td>
                            <td className="py-1 pr-2 text-destructive max-w-[300px] truncate">{p.error || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm bg-muted/30 rounded p-3">
                  <Kv label="FA total (non-cancelled)" value={r.reconciliation?.fa_total_non_cancelled} mono />
                  <Kv label="PMS total (non-cancelled)" value={r.reconciliation?.pms_total_non_cancelled} mono />
                  <Kv label="Surplus (FA - PMS)" value={r.reconciliation?.surplus} mono />
                  <Kv label="FA nights" value={r.fa_count_basis?.nights} mono />
                  <Kv label="Exact matches (Hostaway id)" value={r.reconciliation?.exact_matches_count} mono />
                  <Kv label="Mapped-stay matches (duplicated)" value={r.reconciliation?.mapped_stay_matches_count} mono />
                  <Kv label="PMS cancelled count" value={r.pms_count_basis?.cancelled_count} mono />
                  <Kv label="PMS-only rows (missing-booking!)" value={r.reconciliation?.pms_only_non_cancelled_total} mono />
                </div>

                {r.reconciliation?.class_subtotals && Object.keys(r.reconciliation.class_subtotals).length > 0 && (
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Surplus classification</div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      {Object.entries(r.reconciliation.class_subtotals).map(([cls, count]) => (
                        <div key={cls} className="flex items-center gap-2">
                          <Badge variant={CLASS_BADGE_VARIANT[cls] || 'outline'} className="text-xs">
                            {count}
                          </Badge>
                          <span className="text-xs">{CLASS_LABELS[cls] || cls}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {r.exact_matches?.length > 0 && (
                  <div>
                    <div className="text-sm font-medium mb-1">
                      Exact matches -- Hostaway id ({r.exact_matches.length})
                    </div>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="py-1 pr-2">Hostaway ID</th>
                            <th className="py-1 pr-2">Property</th>
                            <th className="py-1 pr-2">Check-in</th>
                            <th className="py-1 pr-2">FA guest</th>
                            <th className="py-1 pr-2">PMS guest</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.exact_matches.map((row, i) => (
                            <tr key={i} className="border-b last:border-b-0">
                              <td className="py-1 pr-2 font-mono">{row.hostaway_id}</td>
                              <td className="py-1 pr-2">{row.property_name || row.property_id}</td>
                              <td className="py-1 pr-2">{row.check_in}</td>
                              <td className="py-1 pr-2 max-w-[120px] truncate">{row.fa_guest_name}</td>
                              <td className="py-1 pr-2 max-w-[120px] truncate">{row.pms_guest_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {r.mapped_stay_matches?.length > 0 && (
                  <div>
                    <div className="text-sm font-medium mb-1">
                      Mapped-stay matches -- duplicated across systems ({r.mapped_stay_matches.length})
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Matched on (Hostaway listing, check-in date, nights) via hostaway_listing_map --
                      guest name shown for eyeballing only, never used to match.
                    </p>
                    <p className="text-xs text-muted-foreground mb-1">
                      FA status/check-out shown next to the live PMS status/check-out so a
                      mis-map vs a ghost-booking call can be made from this card directly.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="py-1 pr-2">Smoobu ext. ID</th>
                            <th className="py-1 pr-2">Hostaway ID</th>
                            <th className="py-1 pr-2">Property</th>
                            <th className="py-1 pr-2">Check-in</th>
                            <th className="py-1 pr-2">Nights</th>
                            <th className="py-1 pr-2">FA guest</th>
                            <th className="py-1 pr-2">PMS guest</th>
                            <th className="py-1 pr-2">Name match</th>
                            <th className="py-1 pr-2">FA status</th>
                            <th className="py-1 pr-2">FA check-out</th>
                            <th className="py-1 pr-2">PMS status</th>
                            <th className="py-1 pr-2">PMS check-out</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.mapped_stay_matches.map((row, i) => (
                            <tr key={i} className="border-b last:border-b-0" title={row.detail}>
                              <td className="py-1 pr-2 font-mono">{row.smoobu_external_id}</td>
                              <td className="py-1 pr-2 font-mono">{row.hostaway_id}</td>
                              <td className="py-1 pr-2">{row.property_name || row.property_id}</td>
                              <td className="py-1 pr-2">{row.check_in}</td>
                              <td className="py-1 pr-2">{row.nights}</td>
                              <td className="py-1 pr-2 max-w-[120px] truncate">{row.fa_guest_name}</td>
                              <td className="py-1 pr-2 max-w-[120px] truncate">{row.pms_guest_name}</td>
                              <td className="py-1 pr-2">
                                <Badge variant={SIMILARITY_BADGE_VARIANT[row.guest_name_display_similarity] || 'outline'} className="text-xs">
                                  {row.guest_name_display_similarity}
                                </Badge>
                              </td>
                              <td className="py-1 pr-2">{row.fa_status}</td>
                              <td className="py-1 pr-2">{row.fa_check_out}</td>
                              <td className="py-1 pr-2">{row.pms_status}</td>
                              <td className="py-1 pr-2">{row.pms_check_out}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {r.pms_only_rows?.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-destructive mb-1">
                      PMS-only (missing from FA -- {r.pms_only_rows.length})
                    </div>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="py-1 pr-2">Hostaway ID</th>
                            <th className="py-1 pr-2">Guest</th>
                            <th className="py-1 pr-2">Property</th>
                            <th className="py-1 pr-2">Check-in</th>
                            <th className="py-1 pr-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.pms_only_rows.map((row, i) => (
                            <tr key={i} className="border-b last:border-b-0">
                              <td className="py-1 pr-2 font-mono">{row.hostaway_id}</td>
                              <td className="py-1 pr-2 max-w-[140px] truncate">{row.guest_name}</td>
                              <td className="py-1 pr-2">{row.property_id}</td>
                              <td className="py-1 pr-2">{row.check_in}</td>
                              <td className="py-1 pr-2">{row.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {r.fa_only_rows?.length > 0 && (
                  <div>
                    <div className="text-sm font-medium mb-1">
                      FA-only (the surplus -- {r.fa_only_rows.length})
                    </div>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="py-1 pr-2">Ext. ID</th>
                            <th className="py-1 pr-2">Source</th>
                            <th className="py-1 pr-2">Guest</th>
                            <th className="py-1 pr-2">Property</th>
                            <th className="py-1 pr-2">Check-in</th>
                            <th className="py-1 pr-2">Class</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.fa_only_rows.map((row, i) => (
                            <tr key={i} className="border-b last:border-b-0" title={row.detail}>
                              <td className="py-1 pr-2 font-mono">{row.external_id || 'n/a'}</td>
                              <td className="py-1 pr-2">{row.source}</td>
                              <td className="py-1 pr-2 max-w-[120px] truncate">{row.guest_name}</td>
                              <td className="py-1 pr-2">{row.property_name || row.property_id}</td>
                              <td className="py-1 pr-2">{row.check_in}</td>
                              <td className="py-1 pr-2">
                                <Badge variant={CLASS_BADGE_VARIANT[row.class] || 'outline'} className="text-xs">
                                  {CLASS_LABELS[row.class] || row.class}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {r.internal_duplicate_ids?.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-destructive mb-1">
                      FA-internal duplicate ids ({r.internal_duplicate_ids.length})
                    </div>
                    <div className="text-xs space-y-1">
                      {r.internal_duplicate_ids.map((d, i) => (
                        <div key={i} className="font-mono">
                          {d.source}:{d.external_id} x{d.count} ({d.check_ins.join(', ')})
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {r.property_census && (
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div className="font-medium text-foreground">Property census</div>
                    {r.property_census.fetch_ok ? (
                      <>
                        <div>
                          FA Hostaway properties: {r.property_census.fa_hostaway_property_count} / live listings: {r.property_census.live_listing_count}
                        </div>
                        {r.property_census.fa_properties_absent_from_live_listing_list?.length > 0 && (
                          <div>
                            Absent from live listing list:{' '}
                            {r.property_census.fa_properties_absent_from_live_listing_list
                              .map(p => `${p.property_id}${p.reservations_still_returned_live ? ' (reservations still returned live)' : ''}`)
                              .join(', ')}
                          </div>
                        )}
                      </>
                    ) : (
                      <div>Listings fetch did not complete cleanly; property census skipped.</div>
                    )}
                  </div>
                )}

                {r.hostaway_listing_map_coverage && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">hostaway_listing_map coverage</div>
                    <div className="text-xs text-muted-foreground">
                      A gap here is the operator's fix-list -- it is what silently misclassifies a
                      genuinely-migrated stay as "not yet migrated" elsewhere on this card.
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      <Kv label="FA Smoobu properties unmapped" value={r.hostaway_listing_map_coverage.unmapped_counts?.fa_smoobu_properties_unmapped} mono />
                      <Kv label="Live listings without a map row" value={r.hostaway_listing_map_coverage.unmapped_counts?.live_hostaway_listings_without_map_row} mono />
                    </div>
                    {r.hostaway_listing_map_coverage.smoobu_1016658_resolution && (
                      <div className="text-xs font-mono bg-muted/40 rounded p-2">
                        Smoobu 1016658: {JSON.stringify(r.hostaway_listing_map_coverage.smoobu_1016658_resolution)}
                      </div>
                    )}
                    {r.hostaway_listing_map_coverage.live_listing_coverage?.length > 0 && (
                      <div className="overflow-x-auto">
                        <div className="text-xs font-medium mb-1">Live Hostaway listings</div>
                        <table className="text-xs w-full">
                          <thead>
                            <tr className="border-b text-left text-muted-foreground">
                              <th className="py-1 pr-2">Hostaway listing ID</th>
                              <th className="py-1 pr-2">Mapped Smoobu property</th>
                              <th className="py-1 pr-2">Status</th>
                              <th className="py-1 pr-2">FA recognizes it</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.hostaway_listing_map_coverage.live_listing_coverage.map((row, i) => (
                              <tr key={i} className="border-b last:border-b-0">
                                <td className="py-1 pr-2 font-mono">{row.hostaway_listing_id}</td>
                                <td className="py-1 pr-2 font-mono">{row.mapped_smoobu_property_id || 'n/a'}</td>
                                <td className="py-1 pr-2">
                                  <Badge variant={row.status === 'mapped' ? 'outline' : 'destructive'} className="text-xs">
                                    {row.status}
                                  </Badge>
                                </td>
                                <td className="py-1 pr-2">{String(row.fa_recognizes_this_hostaway_property)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {r.hostaway_listing_map_coverage.fa_smoobu_coverage?.length > 0 && (
                      <div className="overflow-x-auto">
                        <div className="text-xs font-medium mb-1">FA Smoobu properties</div>
                        <table className="text-xs w-full">
                          <thead>
                            <tr className="border-b text-left text-muted-foreground">
                              <th className="py-1 pr-2">Smoobu property ID</th>
                              <th className="py-1 pr-2">Mapped Hostaway listing</th>
                              <th className="py-1 pr-2">Status</th>
                              <th className="py-1 pr-2">Mapped listing still live</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.hostaway_listing_map_coverage.fa_smoobu_coverage.map((row, i) => (
                              <tr key={i} className="border-b last:border-b-0">
                                <td className="py-1 pr-2 font-mono">{row.smoobu_property_id}</td>
                                <td className="py-1 pr-2 font-mono">{row.mapped_hostaway_listing_id || 'n/a'}</td>
                                <td className="py-1 pr-2">
                                  <Badge variant={row.status === 'mapped' ? 'outline' : 'secondary'} className="text-xs">
                                    {row.status}
                                  </Badge>
                                </td>
                                <td className="py-1 pr-2">{row.mapped_listing_still_live === null ? 'n/a' : String(row.mapped_listing_still_live)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
